import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  Typography,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  AllInbox as AllIcon,
  Inbox as UngroupedIcon,
  Archive as ArchiveIcon,
  ExpandMore,
  ChevronRight,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  sidebar: {
    width: 286,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  sectionHeader: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 2),
    textTransform: "uppercase",
  },
  listItem: {
    paddingLeft: (props) => theme.spacing(2 + props.depth * 2),
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  dropTarget: {
    backgroundColor: (props) => `${props.clientColor || "#1976d2"}26`,
  },
  folderIcon: {
    minWidth: 32,
  },
  scenarioCount: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: "auto",
  },
  systemFolder: {
    color: theme.palette.text.secondary,
  },
  actionButton: {
    padding: 4,
    marginLeft: 4,
    opacity: 0,
    transition: "opacity 0.2s",
  },
  editButton: {
    "&:hover": {
      color: (props) => props.clientColor || "#1976d2",
    },
  },
  deleteButton: {
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
  listItemWithActions: {
    "&:hover $actionButton": {
      opacity: 1,
    },
  },
  editInput: {
    flex: 1,
    "& .MuiInputBase-input": {
      fontSize: "0.875rem",
      padding: "4px 8px",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: (props) => props.clientColor || "#1976d2",
    },
  },
  editingItem: {
    paddingRight: theme.spacing(1),
  },
}));

const DroppableFolderItem = ({
  folder,
  depth,
  isSelected,
  isExpanded,
  hasChildren,
  onSelect,
  onToggleExpand,
  onDelete,
  onRename,
  isEditing,
  editingName,
  onEditStart,
  onEditChange,
  onEditSubmit,
  onEditCancel,
  clientColor,
  children,
}) => {
  const classes = useStyles({ depth, clientColor });
  const inputRef = useRef(null);

  const { setNodeRef, isOver } = useDroppable({
    id: `sidebar-folder-${folder.folder_id}`,
    data: { type: "folder", ...folder },
  });

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onEditSubmit();
    } else if (e.key === "Escape") {
      onEditCancel();
    }
  };

  return (
    <>
      <ListItem
        ref={setNodeRef}
        button={!isEditing}
        className={`${classes.listItem} ${classes.listItemWithActions} ${isOver ? classes.dropTarget : ""} ${isEditing ? classes.editingItem : ""}`}
        selected={isSelected}
        onClick={() => !isEditing && onSelect(folder.folder_id)}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(folder.folder_id);
            }}
          >
            {isExpanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box width={28} />
        )}
        <ListItemIcon className={classes.folderIcon}>
          {isExpanded ? (
            <FolderOpenIcon style={{ color: folder.color || "#6B7280" }} />
          ) : (
            <FolderIcon style={{ color: folder.color || "#6B7280" }} />
          )}
        </ListItemIcon>

        {isEditing ? (
          <TextField
            inputRef={inputRef}
            value={editingName}
            onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onEditSubmit}
            className={classes.editInput}
            size="small"
            variant="standard"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <ListItemText
              primary={folder.name}
              primaryTypographyProps={{
                noWrap: true,
                style: { maxWidth: 100 },
              }}
            />
            <span className={classes.scenarioCount}>
              {folder.scenario_count}
            </span>
            {onRename && (
              <IconButton
                size="small"
                className={`${classes.actionButton} ${classes.editButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditStart(folder);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="small"
                className={`${classes.actionButton} ${classes.deleteButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(folder, e.currentTarget);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </ListItem>
      {hasChildren && <Collapse in={isExpanded}>{children}</Collapse>}
    </>
  );
};

DroppableFolderItem.propTypes = {
  folder: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  hasChildren: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onRename: PropTypes.func,
  isEditing: PropTypes.bool,
  editingName: PropTypes.string,
  onEditStart: PropTypes.func,
  onEditChange: PropTypes.func,
  onEditSubmit: PropTypes.func,
  onEditCancel: PropTypes.func,
  clientColor: PropTypes.string,
  children: PropTypes.node,
};

const FolderSidebar = ({
  folders,
  selectedFolderId,
  onFolderSelect,
  onFolderDelete,
  onFolderRename,
  ungroupedCount = 0,
  archivedCount = 0,
  clientColor,
}) => {
  const classes = useStyles({ depth: 0, clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager.sidebar",
  });
  const [expandedIds, setExpandedIds] = useState([]);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [originalName, setOriginalName] = useState("");

  const handleToggleExpand = (folderId) => {
    setExpandedIds((prev) =>
      prev.includes(folderId)
        ? prev.filter((id) => id !== folderId)
        : [...prev, folderId],
    );
  };

  const handleEditStart = (folder) => {
    setEditingFolderId(folder.folder_id);
    setEditingName(folder.name);
    setOriginalName(folder.name);
  };

  const handleEditChange = (value) => {
    setEditingName(value);
  };

  const handleEditSubmit = () => {
    const trimmedName = editingName.trim();
    // Only call rename if the name actually changed
    if (
      editingFolderId &&
      trimmedName &&
      trimmedName !== originalName &&
      onFolderRename
    ) {
      onFolderRename(editingFolderId, trimmedName);
    }
    setEditingFolderId(null);
    setEditingName("");
    setOriginalName("");
  };

  const handleEditCancel = () => {
    setEditingFolderId(null);
    setEditingName("");
    setOriginalName("");
  };

  // Droppable zones for special folders
  const { setNodeRef: setUngroupedRef, isOver: isOverUngrouped } = useDroppable(
    {
      id: "sidebar-ungrouped",
      data: { type: "ungrouped" },
    },
  );

  const { setNodeRef: setArchiveRef, isOver: isOverArchive } = useDroppable({
    id: "sidebar-archive",
    data: { type: "archive" },
  });

  const renderFolder = (folder, depth = 0) => {
    const isExpanded = expandedIds.includes(folder.folder_id);
    const hasChildren = folder.subfolders && folder.subfolders.length > 0;
    const isEditing = editingFolderId === folder.folder_id;

    return (
      <DroppableFolderItem
        key={folder.folder_id}
        folder={folder}
        depth={depth}
        isSelected={selectedFolderId === folder.folder_id}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onSelect={onFolderSelect}
        onToggleExpand={handleToggleExpand}
        onDelete={onFolderDelete}
        onRename={onFolderRename}
        isEditing={isEditing}
        editingName={editingName}
        onEditStart={handleEditStart}
        onEditChange={handleEditChange}
        onEditSubmit={handleEditSubmit}
        onEditCancel={handleEditCancel}
        clientColor={clientColor}
      >
        {folder.subfolders?.map((sub) => renderFolder(sub, depth + 1))}
      </DroppableFolderItem>
    );
  };

  return (
    <Box className={classes.sidebar}>
      <List dense disablePadding>
        {/* All Scenarios */}
        <ListItem
          button
          selected={selectedFolderId === null}
          onClick={() => onFolderSelect(null)}
          className={classes.listItem}
        >
          <Box width={28} />
          <ListItemIcon className={classes.folderIcon}>
            <AllIcon color="action" />
          </ListItemIcon>
          <ListItemText primary={t("all_scenarios")} />
        </ListItem>

        {/* User folders */}
        <Typography className={classes.sectionHeader}>
          {t("folders")}
        </Typography>
        {folders.map((folder) => renderFolder(folder, 0))}

        <Divider style={{ margin: "8px 0" }} />

        {/* Ungrouped */}
        <ListItem
          ref={setUngroupedRef}
          button
          selected={selectedFolderId === -1}
          onClick={() => onFolderSelect(-1)}
          className={`${classes.listItem} ${isOverUngrouped ? classes.dropTarget : ""}`}
        >
          <Box width={28} />
          <ListItemIcon className={classes.folderIcon}>
            <UngroupedIcon color="action" />
          </ListItemIcon>
          <ListItemText primary={t("ungrouped")} />
          <span className={classes.scenarioCount}>{ungroupedCount}</span>
        </ListItem>

        {/* Archive (system folder) */}
        <ListItem
          ref={setArchiveRef}
          button
          selected={selectedFolderId === "archive"}
          onClick={() => onFolderSelect("archive")}
          className={`${classes.listItem} ${classes.systemFolder} ${isOverArchive ? classes.dropTarget : ""}`}
        >
          <Box width={28} />
          <ListItemIcon className={classes.folderIcon}>
            <ArchiveIcon color="action" />
          </ListItemIcon>
          <ListItemText primary={t("archive")} />
          <span className={classes.scenarioCount}>{archivedCount}</span>
        </ListItem>
      </List>
    </Box>
  );
};

FolderSidebar.propTypes = {
  folders: PropTypes.array.isRequired,
  selectedFolderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onFolderSelect: PropTypes.func.isRequired,
  onFolderDelete: PropTypes.func,
  onFolderRename: PropTypes.func,
  ungroupedCount: PropTypes.number,
  archivedCount: PropTypes.number,
  clientColor: PropTypes.string,
};

export default FolderSidebar;
